import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";

const TURQUOISE = "#00B4A6";
const TURQUOISE_DARK = "#00796B";
const GOLD = "#D4AF37";
const GOLD_DARK = "#8A6500";
const BURGUNDY = "#7A1232";
const BURGUNDY_DARK = "#4A071D";
const INK = "#071313";
const WHITE = "#FFFFFF";
const MUTED = "rgba(7, 19, 19, 0.68)";
const LINE = "rgba(7, 19, 19, 0.10)";

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";

const socialImpactText = `
Social Impact

Last updated: 11 June 2026

Art For My Room was created with a simple belief: art should not sit unseen in storage, on forgotten websites, or behind difficult buying decisions. It should find the room where it makes sense, and the artist behind it should have a better chance of being discovered.

Our social impact commitment is focused on helping artists, buyers, students and local creative communities through practical tools, fairer discovery and clearer access to original art.

1. Supporting artists with better discovery

Many artists do not struggle because their work lacks value. They struggle because their work is difficult to discover, difficult to explain online, or difficult for buyers to imagine inside a real home.

Art For My Room helps artists by:

- allowing artists to list original paintings and sculptures
- helping buyers discover artworks through real room photos
- creating public artwork pages for each listed piece
- creating artist pages where buyers can browse more work by the same artist
- offering negotiation for original paintings and sculptures
- keeping listing free and charging only 10% commission when sold
- providing tools that help artists describe and present their paintings better

The aim is to make online discovery feel less like a cold catalogue and more like a bridge between the artist, the artwork and the buyer’s space.

2. Making art easier for buyers to understand

Buying art can feel intimidating. People may like a painting but still wonder:

Will it work in my room?
Will the colours fit?
Is the size right?
Will it feel too strong, too quiet, too large or too small?

Art For My Room helps buyers start with something familiar: their own room.

By uploading a room photo, buyers can discover paintings, prints, sculptures and decorative objects that match the room’s colour, mood, scale and atmosphere. This makes art buying more visual, more personal and less dependent on guesswork.

3. Helping independent and local artists

Art For My Room is especially interested in supporting independent artists and local creative communities.

A buyer may be more likely to purchase original art when they can:

- understand the artwork clearly
- see where it may fit
- communicate through a structured offer process
- discover artists through public artist pages
- find work that feels connected to their home

We want to help artists become more visible without forcing them into expensive listing fees, complicated systems or traditional gatekeeping.

4. Reducing barriers to selling art online

Selling art online often requires many skills beyond making art. Artists may need to write titles, descriptions, SEO tags, social media captions, price notes, listing copy and buyer-friendly explanations.

Our free Painting Analyzer is designed to support artists with practical, action-ready content. It can help painters create:

- title ideas
- artwork descriptions
- SEO tags
- colour notes
- mood notes
- pricing guidance
- social media captions
- listing content

This is not meant to replace the artist’s voice. It is meant to help the artist present their work with more confidence and less friction.

5. Encouraging fairer access to original art

Original art can be expensive, and that is understandable. Artists invest time, materials, skill, imagination and years of practice into their work.

At the same time, buyers may need a softer way to approach a purchase. That is why Art For My Room supports a structured negotiation flow for original paintings and sculptures.

This allows buyers to make offers while still respecting the artist’s listed price and decision. The goal is not to devalue original art, but to create a clearer and more human path between interest and purchase.

6. Supporting creative learning

Art For My Room has developed through experimentation, student research, AI-assisted development and practical website building. We value learning by making.

Where possible, we want the project to create opportunities for:

- students interested in art, technology and digital business
- early-career creatives
- artists learning how to present work online
- people exploring AI tools in ethical and useful ways
- small creative communities looking for visibility

The long-term aim is to become more than a marketplace. We want Art For My Room to become a useful creative platform where artists, buyers and learners all gain something meaningful.

7. Human creativity first

AI is used on Art For My Room to support discovery, presentation and room matching. It is not here to replace artists.

We believe AI should help people:

- understand artwork better
- reduce repetitive admin
- discover suitable art faster
- make creative work easier to present
- connect artists and buyers more effectively

The artwork remains human. The artist remains central. The technology is only the lantern.

8. Responsible growth

As Art For My Room grows, we want to measure success not only through sales, but through useful creative impact.

We care about:

- helping artists get discovered
- making art buying easier and more visual
- encouraging local and independent art
- reducing unnecessary listing barriers
- giving painters useful free tools
- creating a respectful buyer and artist experience
- building a website that feels trustworthy, elegant and human

9. Our continuing commitment

Our social impact work will continue to evolve. As the platform grows, we aim to listen to artists, buyers and creative partners to understand what is genuinely useful.

We will keep improving the website so that more art can be seen, more artists can be supported, and more rooms can become personal, expressive and alive with creative work.

10. Contact

For questions about our social impact work, partnerships or artist support, contact:

Art For My Room
Email: info@artformyroom.com
`;

const impactCards = [
  {
    icon: <PaletteRoundedIcon />,
    title: "Artists first",
    text: "Free listing for original paintings and sculptures, with only 10% commission when sold.",
  },
  {
    icon: <GroupsRoundedIcon />,
    title: "Better discovery",
    text: "Buyers discover artworks through real rooms, not just endless scrolling.",
  },
  {
    icon: <AutoAwesomeRoundedIcon />,
    title: "Useful AI",
    text: "AI supports descriptions, tags, room matching and presentation, while human creativity stays central.",
  },
  {
    icon: <SchoolRoundedIcon />,
    title: "Creative learning",
    text: "The platform encourages practical learning around art, technology, data and digital business.",
  },
];

export default function SocialImpactPage() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#FBFFFE",
        color: INK,
        fontFamily: BODY_FONT,
        background: `
          radial-gradient(circle at 8% 8%, ${alpha(TURQUOISE, 0.14)}, transparent 30%),
          radial-gradient(circle at 90% 12%, ${alpha(GOLD, 0.18)}, transparent 28%),
          radial-gradient(circle at 86% 82%, ${alpha(BURGUNDY, 0.12)}, transparent 34%),
          linear-gradient(180deg, #ffffff 0%, #f6fffd 50%, #fff8e8 100%)
        `,
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Button
          component={RouterLink}
          to="/"
          startIcon={<ArrowBackRoundedIcon />}
          sx={{
            mb: 3,
            color: TURQUOISE_DARK,
            fontWeight: 800,
            textTransform: "none",
          }}
        >
          Back home
        </Button>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 8,
            overflow: "hidden",
            border: `1px solid ${LINE}`,
            boxShadow: "0 24px 80px rgba(7, 19, 19, 0.10)",
            bgcolor: WHITE,
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 5 },
              color: WHITE,
              background: `
                radial-gradient(circle at 12% 10%, ${alpha(TURQUOISE, 0.36)}, transparent 32%),
                radial-gradient(circle at 88% 15%, ${alpha(GOLD, 0.28)}, transparent 30%),
                linear-gradient(135deg, ${TURQUOISE_DARK}, ${BURGUNDY_DARK})
              `,
            }}
          >
            <Stack spacing={2}>
              <Chip
                icon={<VolunteerActivismRoundedIcon />}
                label="Commitments"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: alpha(WHITE, 0.16),
                  color: WHITE,
                  fontWeight: 900,
                  border: `1px solid ${alpha(WHITE, 0.22)}`,
                  "& .MuiChip-icon": { color: WHITE },
                }}
              />

              <Typography
                component="h1"
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: { xs: "2.45rem", md: "4.5rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.075em",
                  lineHeight: 0.95,
                  maxWidth: 900,
                }}
              >
                Social Impact
              </Typography>

              <Typography
                sx={{
                  color: alpha(WHITE, 0.84),
                  lineHeight: 1.85,
                  maxWidth: 820,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                }}
              >
                How Art For My Room aims to support artists, buyers, creative learning
                and fairer discovery for original art.
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 3, md: 5 } }}>
            <Grid container spacing={2.4} sx={{ mb: 4 }}>
              {impactCards.map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.title}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 2.4,
                      borderRadius: 5,
                      border: `1px solid ${LINE}`,
                      bgcolor: "#FBFFFE",
                    }}
                  >
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 4,
                        display: "grid",
                        placeItems: "center",
                        mb: 1.5,
                        color: WHITE,
                        background: `linear-gradient(135deg, ${TURQUOISE}, ${GOLD}, ${BURGUNDY})`,
                      }}
                    >
                      {card.icon}
                    </Box>

                    <Typography
                      sx={{
                        fontWeight: 950,
                        fontFamily: HEADING_FONT,
                        fontSize: "1.2rem",
                        letterSpacing: "-0.035em",
                        mb: 0.8,
                      }}
                    >
                      {card.title}
                    </Typography>

                    <Typography sx={{ color: MUTED, lineHeight: 1.65 }}>
                      {card.text}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ mb: 4, borderColor: LINE }} />

            <Typography
              component="article"
              sx={{
                whiteSpace: "pre-line",
                color: MUTED,
                lineHeight: 1.85,
                fontSize: "1rem",
                maxWidth: 850,
                mx: "auto",
                "&::first-line": {
                  color: INK,
                  fontWeight: 950,
                  fontSize: "1.4rem",
                },
              }}
            >
              {socialImpactText}
            </Typography>

            <Divider sx={{ my: 4, borderColor: LINE }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="center"
            >
              <Button
                component={RouterLink}
                to="/artist-launch"
                variant="contained"
                startIcon={<StorefrontRoundedIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: TURQUOISE_DARK,
                  "&:hover": { bgcolor: TURQUOISE },
                }}
              >
                Upload original art
              </Button>

              <Button
                component={RouterLink}
                to="/artist-room-matcher"
                variant="outlined"
                startIcon={<PublicRoundedIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  color: BURGUNDY,
                  borderColor: alpha(BURGUNDY, 0.35),
                }}
              >
                See the buyer experience
              </Button>

              <Button
                component={RouterLink}
                to="/artist-painting-analyzer"
                variant="outlined"
                startIcon={<HandshakeRoundedIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  color: GOLD_DARK,
                  borderColor: alpha(GOLD, 0.45),
                }}
              >
                Use artist tools
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
